/**
 * Typography constants for consistent text styling across the application
 * These constants define common font sizes and weights used in headings and text
 */

// Font sizes (Tailwind CSS classes)
export const FONT_SIZES = {
	xs: "text-xs", // 0.75rem / 12px
	sm: "text-sm", // 0.875rem / 14px
	base: "text-base", // 1rem / 16px
	lg: "text-lg", // 1.125rem / 18px
	xl: "text-xl", // 1.25rem / 20px
	"2xl": "text-2xl", // 1.5rem / 24px
	"3xl": "text-3xl", // 1.875rem / 30px
	"4xl": "text-4xl", // 2.25rem / 36px
	"5xl": "text-5xl", // 3rem / 48px
	"6xl": "text-6xl", // 3.75rem / 60px
	"7xl": "text-7xl", // 4.5rem / 72px
} as const;

// Font weights (Tailwind CSS classes)
export const FONT_WEIGHTS = {
	thin: "font-thin", // 100
	extralight: "font-extralight", // 200
	light: "font-light", // 300
	normal: "font-normal", // 400
	medium: "font-medium", // 500
	semibold: "font-semibold", // 600
	bold: "font-bold", // 700
	extrabold: "font-extrabold", // 800
	black: "font-black", // 900
} as const;

// Text colors (Tailwind CSS classes)
export const TEXT_COLORS = {
	foreground: "text-foreground", // Theme-aware text color (dark in light mode, light in dark mode)
	primary: "text-primary", // Brand primary color
	slate900: "text-slate-900", // Deep black/dark gray
	slate800: "text-slate-800",
	slate700: "text-slate-700",
	slate600: "text-slate-600",
	gray600: "text-gray-600",
	gray500: "text-gray-500",
} as const;

// Common heading style combinations
export const HEADING_STYLES = {
	h1: {
		size: FONT_SIZES["5xl"],
		weight: FONT_WEIGHTS.bold,
		color: TEXT_COLORS.foreground,
		className: `${FONT_SIZES["5xl"]} ${FONT_WEIGHTS.bold} ${TEXT_COLORS.foreground} leading-tight tracking-tight`,
	},
	h2: {
		size: FONT_SIZES["4xl"],
		weight: FONT_WEIGHTS.bold,
		className: `${FONT_SIZES["4xl"]} ${FONT_WEIGHTS.bold}`,
	},
	h3: {
		size: FONT_SIZES["3xl"],
		weight: FONT_WEIGHTS.bold,
		className: `${FONT_SIZES["3xl"]} ${FONT_WEIGHTS.bold}`,
	},
	h4: {
		size: FONT_SIZES["2xl"],
		weight: FONT_WEIGHTS.bold,
		className: `${FONT_SIZES["2xl"]} ${FONT_WEIGHTS.bold}`,
	},
	h5: {
		size: FONT_SIZES.xl,
		weight: FONT_WEIGHTS.bold,
		className: `${FONT_SIZES.xl} ${FONT_WEIGHTS.bold}`,
	},
	h6: {
		size: FONT_SIZES.lg,
		weight: FONT_WEIGHTS.bold,
		className: `${FONT_SIZES.lg} ${FONT_WEIGHTS.bold}`,
	},
} as const;

// Section title style (commonly used for FAQ, Features, etc.)
export const SECTION_TITLE = {
	size: FONT_SIZES["4xl"],
	weight: FONT_WEIGHTS.bold,
	color: TEXT_COLORS.foreground, // Default to theme-aware foreground color (black in light mode)
	className: `${FONT_SIZES["4xl"]} ${FONT_WEIGHTS.bold} ${TEXT_COLORS.foreground}`,
} as const;
